import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { withAppHOC } from '../../hoc';
import PageHeading from '../../components/PageHeader/PageHeader';
import OverlayLoader from '../../components/OverlayLoader/OverlayLoader';
import {
  FormContainer,
  Form,
  Fieldset,
  Label,
  TextInput,
  MessageBox,
  UpdateButton,
  ResetButton,
} from '../../components/FormElements';
import API from '../../api';
import { API_RESPONSE_TYPES } from '../../constants';
import modulesMapper from '../../constants/modules-mapping';

const StyledModuleContainer = styled.section`
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  border: 1px solid #e5e5e5;
  border-bottom: 0px;
  margin: 0px;
  padding: 0px;
  &:last-child {
    border-bottom: 1px solid #e5e5e5;
  }
  @media (max-width: 1200px) {
    padding: 10px;
    display: block;
    width: calc(100% - 22px);
    label {
      margin: 10px 0px;
    }
  }
`;

const StyledModuleNameContainer = styled.section`
  width: auto;
  min-width: 25%;
`;

const StyledModulePermissionsContainer = styled.section`
  width: auto;
  height: auto;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  border-left: 1px solid #e5e5e5;
  @media (max-width: 1200px) {
    display: block;
    border-left: 0px;
  }
`;

const StyledModuleCheckBoxContainer = styled.section`
  display: flex;
  margin: 0px;
  padding: 10px;
  @media (max-width: 1200px) {
    display: block;
    border: 0px;
    padding: 0px;
  }
`;

const StyledModuleName = styled.h3`
  font-size: 14px;
  font-weight: normal;
  color: #000000;
  margin: 0px;
  padding: 10px;
  @media (max-width: 1200px) {
    padding: 0px 0px 10px 0px;
  }
`;

const StyledLabel = styled(Label)`
  display: inline-block;
  margin: 0px;
  padding: 0px;
`;

const RolesAndPermissionsUpdate = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [roleName, setRoleName] = useState('');
  const [permissions, setPermissions] = useState({});
  const [originalFormData, setOriginalFormData] = useState({});
  const { hostid, id } = useParams();

  const resetResponseData = () => {
    setIsLoading(false);
    setResponseStatus('');
    setResponseMessage('');
  };

  const resetForm = () => {
    const { roleName, permissions } = originalFormData;
    setRoleName(roleName);
    setPermissions(permissions);
  };

  const onClickCheckBox = (module, permission) => {
    const { moduleName } = module;
    let newPermissions = { ...permissions };

    if (newPermissions[moduleName]) {
      let newModulePermissions = [...newPermissions[moduleName]];
      if (newModulePermissions.includes(permission)) {
        newModulePermissions = newModulePermissions.filter(
          (item) => item !== permission
        );
        newPermissions = {
          ...newPermissions,
          [moduleName]: [...newModulePermissions],
        };
      } else {
        newModulePermissions.push(permission);
        newPermissions = {
          ...newPermissions,
          [moduleName]: [...newModulePermissions],
        };
      }
    } else {
      newPermissions = { ...newPermissions, [moduleName]: [permission] };
    }

    setPermissions(newPermissions);
  };

  const readData = useCallback(() => {
    setIsLoading(true);
    setResponseStatus('');
    setResponseMessage('');
    API.get(`/roles-and-permissions/${id}?hostId=${hostid}`)
      .then((response) => {
        const { status, message, data } = response.data;
        if (status === API_RESPONSE_TYPES.FAILURE) {
          setResponseStatus(status);
          setResponseMessage(message);
        } else {
          const { roleName, permissions } = data[0];
          setRoleName(roleName);
          setPermissions(JSON.parse(permissions));
          setOriginalFormData({
            roleName,
            permissions: JSON.parse(permissions),
          });
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILURE);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [hostid, id]);

  const onSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setResponseStatus('');
    setResponseMessage('');
    const formData = {
      roleName,
      permissions: JSON.stringify(permissions),
    };
    API.put(`/roles-and-permissions/${id}?hostId=${hostid}`, formData)
      .then((response) => {
        const { status, message } = response.data;
        setResponseStatus(status);
        setResponseMessage(message);
        if (status === API_RESPONSE_TYPES.SUCCESS) {
          setOriginalFormData(formData);
          setTimeout(() => {
            resetResponseData();
            window.location.href = `/roles-and-permissions/${hostid}`;
          }, 3000);
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILURE);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    readData();
  }, [readData]);

  return (
    <>
      <PageHeading {...props} />
      <FormContainer>
        <Form method='POST' action='#' onSubmit={onSubmit}>
          <Fieldset>
            <Label required>Role Name</Label>
            <TextInput
              value={roleName}
              onChange={setRoleName}
              placeholder='Please enter role name'
              maxLength={100}
              disabled={isLoading === true}
            />
          </Fieldset>
          <Fieldset>
            {modulesMapper &&
              modulesMapper.map((moduleItem) => {
                return (
                  <StyledModuleContainer key={moduleItem.moduleName}>
                    <StyledModuleNameContainer>
                      <StyledModuleName>
                        {moduleItem.moduleName}
                      </StyledModuleName>
                    </StyledModuleNameContainer>
                    <StyledModulePermissionsContainer>
                      {moduleItem.permissions &&
                        moduleItem.permissions.map((permissionItem) => {
                          return (
                            <StyledModuleCheckBoxContainer
                              key={permissionItem.permission}
                            >
                              <input
                                type='checkbox'
                                id={`${moduleItem.moduleName}_${permissionItem.permission}`}
                                name={`${moduleItem.moduleName}_${permissionItem.permission}`}
                                checked={
                                  permissions[moduleItem.moduleName]?.includes(
                                    permissionItem.permission
                                  ) || false
                                }
                                onChange={() =>
                                  onClickCheckBox(
                                    moduleItem,
                                    permissionItem.permission
                                  )
                                }
                                disabled={isLoading === true}
                              />
                              <StyledLabel
                                htmlFor={`${moduleItem.moduleName}_${permissionItem.permission}`}
                              >
                                {permissionItem.permission}
                              </StyledLabel>
                            </StyledModuleCheckBoxContainer>
                          );
                        })}
                    </StyledModulePermissionsContainer>
                  </StyledModuleContainer>
                );
              })}
          </Fieldset>
          {responseStatus && (
            <Fieldset>
              <MessageBox status={responseStatus} message={responseMessage} />
            </Fieldset>
          )}
          <Fieldset>
            <UpdateButton disabled={isLoading === true} type='submit'>
              Update
            </UpdateButton>
            <ResetButton
              disabled={isLoading === true}
              type='button'
              onClick={resetForm}
            >
              Reset
            </ResetButton>
          </Fieldset>
        </Form>
        <OverlayLoader showLoader={isLoading} />
      </FormContainer>
    </>
  );
};

export default withAppHOC(RolesAndPermissionsUpdate);
